import React, { useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import clsx from 'clsx';
import { IState } from 'services/root-reducer';
import { Checkbox, Button, Icons } from 'components';
import IconList from 'static/images/icon-list.svg';
import IconSelect from 'static/images/icon-select-site.svg';
import IconArrow from 'static/images/icon-arrow-right.svg';
import IconArrowLite from 'static/images/icon-arrow-lite.svg';
import {
    getOperation,
    getOperationData,
    getOmjsProjectInstance,
    getHints,
    getOmjsViewLayer,
    getCachedFullContainers
} from 'selectors';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { actions as containersActions } from 'services/containers/containers-service';
import { actions as appActions } from 'services/app/app-service';
import { routes } from 'routes';
import { getContainerIcon, htmlDecode } from 'utils';
import { HelpButtonWithPopover } from 'components/help-button';
import Slider from 'rc-slider';
import { opacityLayers } from 'project-constants';
import { IContainer } from 'types/omjs/project';
import { isMobileOnlyFixed } from 'utils/is-fixed-ismobileonly';
import { isObjectListAvailable, isLegendAvailable } from '../categories-config';
import './style.scss';
import { LegendItemWithPopover } from './legend-item';

interface ICategoryItemProps {
    container: omjs.IContainer;
    allContainers: IState['operations']['CONTAINERS']['data'];
    project: omjs.Project;
    category: string;
    layersConfig: IState['app']['config']['publication']['layers_config'];
    hideHints?: boolean;
}

const mapStateToProps = (state: IState) => ({
    initialize: getOperation(state, 'APP_INITIALIZE'),
    allContainers: getOperationData(state, 'CONTAINERS'),
    project: getOmjsProjectInstance(state),
    layersConfig: state.app.config.publication.layers_config
});

const getChildsRecursive = (allContainers: IState['operations']['CONTAINERS']['data'], parentId: number) => {
    let childs = allContainers.filter(c => c.parent_id === parentId);
    for (let child of childs) {
        childs = [ ...childs, ...getChildsRecursive(allContainers, child.id) ];
    }
    return childs;
};

const containersWithDivider = [ 'plots', 'parks' ];

const CategoryItemComponent: React.FC<ICategoryItemProps> = (props): JSX.Element => {
    const {
        container,
        allContainers,
        project,
        category,
        hideHints = false
    } = props;

    const isFolder = container.type === 'folder';
    const withDivider = containersWithDivider.includes(container.code);
    const showListBtn = isObjectListAvailable(container);
    const showLegend = isLegendAvailable(container);
    const { t } = useTranslation();

    const genPlanes = useSelector((state: IState) => getOperationData(state, 'GEN_PLANES'));
    const folderState = useSelector((state: IState) => getOperationData(state, 'FOLDER_STATE'));
    const hints = useSelector(getHints);
    let collapsed = !folderState[container.id] && container.code !== 'gen_plans';

    let viewLayers = [];
    let layerOpacity = null;
    if (opacityLayers.includes(container.code) || withDivider) {
        let childs = [];
        if (!withDivider) {
            childs = getChildsRecursive(allContainers, container.id);
        } else {
            childs = getChildsRecursive(allContainers, container.id);
            const renderTypes = opacityLayers.includes(container.code) ? [ 0, 1 ] : [ 0 ];
            childs = childs.filter(ch => renderTypes.includes(ch.render_type) && ch.type !== 'folder');
        }

        const vLayersSet = new Set();
        childs.forEach(child => {
            const childLayer = useSelector((state: IState) => getOmjsViewLayer(state, child.code));
            if (childLayer) {
                vLayersSet.add(childLayer);
            }
        });
        viewLayers = [ ...vLayersSet ];
        if (viewLayers.length) {
            layerOpacity = viewLayers[0].getMapLayer().getOpacity() * 100;
        }
    }

    const [ opacity, setOpacity ] = useState<number | null>(layerOpacity);

    let filteredCount = 0;
    let totalCount = 0;
    if (container.code === 'plots' || (showListBtn && container.selected)) {
        const conts = allContainers.filter(cont => cont.parent_id === container.id);
        const fullConts = project.getCachedContainers();
        filteredCount = conts.reduce((amount, item) => {
            let count: number = item.objectsCount as number;
            let cont = fullConts.find(c => c.get('code') === item.code);
            totalCount += (cont.get('selected') && cont.objects?.getLength) ? cont.objects.getLength() : 0;
            return amount + (cont.get('selected') ? count : 0);
        }, 0);
    }

    const renderLegend = (parent) => {
        const childContainers = allContainers.filter(cont => (
            cont.parent_id === parent.id && cont.type !== 'folder'
        ));

        return (
            <div className="category-panel-item__controls-legend">
                {childContainers.map((cont: omjs.IContainer) => (
                    <LegendItemWithPopover
                        container={cont}
                        key={cont.id}
                        popoverProps={{
                            className: 'popover-no-events',
                            white: true
                        }}
                    >
                        <div className='category-panel-item__controls-legend-popover'>
                            { cont.name }
                        </div>
                    </LegendItemWithPopover>
                ))}
            </div>
        );
    };

    const HelpButton = React.useMemo(() => {
        const hint = hints.find(obj => obj.get('code') === container.code);
        if (hint) {
            const { title, text } = hint['_attributes'];
            return (
                <HelpButtonWithPopover
                    popoverProps={{
                        placement: 'right',
                        className: 'help-button-popover'
                    }}
                    offset={[ 0, 20 ]}
                >
                    <div className="header">{ htmlDecode(title) }</div>
                    <div className="body">{ htmlDecode(text) }</div>
                </HelpButtonWithPopover>
            );
        }

        return null;
    }, [ container.code ]);

    const handleSwitchContainer = (containerId: number, selected: boolean) => {
        containersActions.setContainerSelected({
            id_or_code: containerId, selected: selected
        });
    };

    const renderDetailedFilterButton = (text: string, action: () => void) => {
        let objectsCountLabel = (
            totalCount > filteredCount
                ? ` | ${filteredCount} / ${totalCount}`
                : ` | ${totalCount}`
        );
        return (
            <div className="category-panel-item__controls">
                <Button
                    // icon={<IconSelect />}
                    text={t(text) + objectsCountLabel}
                    onClick={() => {
                        if (!container.selected) {
                            handleSwitchContainer(container.id, true);
                        }
                        action();
                    }}
                />
            </div>
        );
    };

    const handleListClick = (layerCode: string) => {
        if (!container.selected) {
            handleSwitchContainer(container.id, true);
        }
        routes.redirect(routes.list, { layerCode }, true);
    };

    const handleOpacityChange = (val: number) => {
        viewLayers.forEach(layer => {
            layer.getMapLayer().setOpacity(val / 100);
        });
        setOpacity(val);
    };

    const detailedFilterText = useMemo(
        () => (container.code === 'plots' ? 'Подбор площадки' : 'Список'),
        [ container.code ]
    );

    const detailedFilterAction = useMemo(() => (container.code === 'plots'
        ? () => appActions.showObjectFiltersWindow()
        : () => handleListClick(container.code)), [ container.code ]);

    const showDetailedFilterButton = useMemo(
        () => container.code === 'plots' || (showListBtn && container.selected && (totalCount > filteredCount)),
        [ container.code, showListBtn, totalCount, filteredCount ]
    );

    const getChilds = (parent) => allContainers.filter(cont => {
        if (i18next.language !== 'ru' && cont.code === 'productive_agricultural_land') return false;
        return cont.parent_id === parent.id;
    });

    const renderChildItem = (child, noCount, renderedChilds = null) => (
        <li className="container-item" key={child.id}>
            <div className="container-item__wrapper">
                {child.type === 'folder' && (
                    <div
                        className={clsx('container-item__expander', folderState[child.id] && 'expanded')}
                        onClick={() => appActions.setFolderState({
                            id: child.id,
                            state: !folderState[child.id]
                        })}
                    >
                        <IconArrowLite />
                    </div>
                )}
                <label
                    key={child.id}
                    className={clsx(
                        'container-item__label',
                        (noCount && child.code !== 'gen_plany_koordinaty') && 'no-count'
                    )}
                >
                    <Checkbox
                        checked={child.selected}
                        onChange={() => handleSwitchContainer(child.id, !child.selected)}
                    />
                    {child.type !== 'folder' && (
                        <div className="container-item__icon-wrapper">
                            <img
                                className="container-item__icon"
                                src={getContainerIcon(child)}
                                alt=""
                            />
                        </div>
                    )}
                    <div className="container-item__text">
                        {child.name}
                    </div>
                    {child.objectsCount !== false && (
                        <div
                            className={clsx('container-item__count', {
                                'not-visible': !child.selected
                            })}
                        >
                            {child.objectsCount}
                        </div>
                    )}
                </label>
            </div>
            {renderedChilds}
        </li>
    );

    const renderChilds = (parent, childs_, noCount = false) => {
        let childContainers = [ ...childs_ ];

        const withGenPlanes = parent.code === 'gen_plans';
        if (withGenPlanes && genPlanes) {
            childContainers = childContainers.filter(child => (
                !(child.code in genPlanes.roots)
                || genPlanes.roots[child.code].active
            ));
            // console.log('genplanes childContainers', childContainers);
        }

        const childs = [];
        if (withDivider) {
            const objectChilds = childContainers.filter((ch) => ch.render_type === 1 || ch.type === 'folder');
            const cartoChilds = childContainers.filter((ch) => ch.render_type === 0 && ch.type !== 'folder');
            objectChilds.forEach((child) => childs.push(renderChildItem(
                child,
                noCount,
                child.type === 'folder' && renderChilds(child, getChilds(child), noCount)
            )));
            childs.push(<div className="category-panel__divider" key="divider" />);
            if (!opacityLayers.includes(parent.code)) {
                childs.push(
                    <div className="layer-opacity-control" key="opacity-control">
                        {t('Прозрачность')}
                        <Slider
                            className="range-filter-control opacity-slider"
                            value={opacity}
                            min={0}
                            max={100}
                            onChange={val => handleOpacityChange(val)}
                        />
                    </div>
                );
            }
            cartoChilds.forEach(child => childs.push(renderChildItem(
                child,
                noCount,
                child.type === 'folder' && renderChilds(child, getChilds(child), noCount)
            )));
        } else {
            childContainers.forEach(child => childs.push(renderChildItem(
                child,
                noCount,
                child.type === 'folder' && renderChilds(child, getChilds(child), noCount)
            )));
        }
        return (
            <ul className={clsx('container-items-box', folderState[parent.id] && !withGenPlanes && 'expanded')}>
                {(opacityLayers.includes(parent.code) && parent.type === 'folder') && (
                    <div className="layer-opacity-control">
                        {t('Прозрачность')}
                        <Slider
                            className="range-filter-control opacity-slider"
                            value={opacity}
                            min={0}
                            max={100}
                            onChange={val => handleOpacityChange(val)}
                        />
                    </div>
                )}
                {childs}
            </ul>
        );
    };

    const categoryChilds = getChilds(container);
    if (container.type === 'folder' && categoryChilds.length === 0) return null;
    return (
        <div
            className={clsx(
                'category-panel-item',
                {
                    'collapsed': isFolder ? collapsed : true,
                    // 'without-list': !showListBtn || container.code === 'gen_plans',
                    'gen-planes': container.code === 'gen_plans'
                }
            )}
        >
            <div className="category-panel-item__header">
                <div className="category-panel-item__name">
                    <div
                        className="category-panel-item__name-container"
                        onClick={() => {
                            if (isFolder) {
                                appActions.setFolderState({ id: container.id, state: !folderState[container.id] });
                            }
                        }}
                    >
                        <div className="category-panel-item__name-checkbox-wrapper">
                            <Checkbox
                                checked={container.selected}
                                onChange={() => handleSwitchContainer(container.id, !container.selected)}
                            />
                        </div>
                        <div className="category-panel-item__name-container-wrapper">
                            <div className='category-panel-item__name-container-wrapper-text'>{container.name}</div>
                            { !hideHints && (
                                <div className="category-panel-item__name-actions-help-wrapper">
                                    {HelpButton}
                                </div>
                            ) }
                            {isFolder && container.code !== 'gen_plans' && (
                                <div className="category-panel-item__name-container-wrapper-expand">
                                    <Icons.IconThickArrowRight
                                        style={{
                                            transform: !collapsed ? 'rotate(90deg)' : ''
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                        <div
                            className={clsx('category-panel-item__name-actions-list')}
                        >
                            {showListBtn && (
                                <IconList
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleListClick(container.code);
                                    }}
                                />
                            )}
                        </div>
                    </div>
                    {(collapsed && (showLegend || container.code === 'plots')) && (
                        <div className="category-panel-item__name-actions">
                            <div className='category-panel-item__name-actions-legend-wrapper'>
                                {showLegend && renderLegend(container)}
                                {showDetailedFilterButton
                                    && renderDetailedFilterButton(detailedFilterText, detailedFilterAction)}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {isFolder && (
                <div className="category-panel-item__body">
                    <div className="category-panel__divider" />
                    {renderChilds(container, categoryChilds, !showListBtn || container.code === 'gen_plans')}
                </div>
            )}

            {!collapsed && (
                <div className="category-panel-item__footer">
                    { showDetailedFilterButton && renderDetailedFilterButton(detailedFilterText, detailedFilterAction) }
                </div>
            )}
            {(opacityLayers.includes(container.code) && container.type !== 'folder') && (
                <div className="layer-opacity-control for-single-layer">
                    {t('Прозрачность')}
                    <Slider
                        className="range-filter-control opacity-slider"
                        value={opacity}
                        min={0}
                        max={100}
                        onChange={val => handleOpacityChange(val)}
                    />
                </div>
            )}
        </div>
    );
};

export const CategoryItem = connect(mapStateToProps)(CategoryItemComponent);
